import {
  Copy,
  ExternalLink,
  FileJson,
  Github,
  Mail,
  Package,
  ShieldCheck
} from "lucide-react";
import type { ELK, ElkEdgeSection, ElkExtendedEdge, ElkNode } from "elkjs/lib/elk-api";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  footerRoutes,
  normalizePath,
  routeForPath,
  site,
  supportLinks
} from "./content";
import { clovisChangelog } from "./generated/clovis-changelog";
import { clovisDocs } from "./generated/clovis-docs";
import { clovisSchema } from "./generated/clovis-schema";
import type { ReactNode } from "react";
import "./styles.css";

type AppProps = {
  initialPath?: string;
  path?: string;
};

type CliHelpRow = {
  term: string;
  description: string;
  raw: string;
};

type CliCommandItem = {
  command: string;
  slug: string;
  path: readonly string[];
  usage: string;
  description: string;
  sections: {
    arguments: readonly CliHelpRow[];
    options: readonly CliHelpRow[];
    commands: readonly CliHelpRow[];
    examples: readonly string[];
    notes: readonly string[];
  };
  help: string;
};

type ToolParameter = {
  name: string;
  type: string;
  required: boolean;
  nullable: boolean;
  variadic: boolean;
  default?: unknown;
  description: string;
};

type ToolCatalogItem = {
  name: string;
  title: string;
  category: string;
  summary: string;
  signature: string;
  returns: unknown;
  parameters: readonly ToolParameter[];
  aliases: Record<string, string>;
  readOnly: boolean;
  destructive: boolean;
  dryRun: boolean;
  defaultDryRun: boolean;
  annotations: unknown;
  safety: unknown;
  docs: {
    summary: string;
    useWhen: readonly string[];
    beforeCalling: readonly string[];
    safety: string;
    returns: string;
    exampleArguments: Record<string, unknown>;
  };
};

type ToolCatalogGroup = {
  category: string;
  tools: readonly ToolCatalogItem[];
};

type SchemaColumn = (typeof clovisSchema.tables)[number]["columns"][number];
type SchemaTable = (typeof clovisSchema.tables)[number];
type SchemaFamily = (typeof clovisSchema.families)[number];
type SchemaRelationship = (typeof clovisSchema.relationships)[number];

type ToolFilter = "all" | "read-only" | "writes" | "dry-run" | "destructive";
type DocsPageKind =
  | "overview"
  | "getting-started"
  | "agents"
  | "imports"
  | "safety"
  | "files"
  | "schema"
  | "changelog"
  | "cli-index"
  | "cli-command"
  | "mcp-index"
  | "mcp-tool";

type DocsPageDescriptor = {
  kind: DocsPageKind;
  path: string;
  label: string;
  title: string;
  description: string;
  command?: CliCommandItem;
  tool?: ToolCatalogItem;
};

const primaryRoutes = [
  { href: "/#can-do", label: "Capabilities" },
  { href: "/docs/", label: "Docs" },
  { href: "/#trust", label: "Security" },
  { href: site.githubUrl, label: "GitHub" }
];

const capabilityItems = [
  {
    label: "Review statements",
    body: "Import files and see what changed before anything is saved.",
    proof: "QFX / OFX / CSV"
  },
  {
    label: "Reconcile records",
    body: "Match statement rows to the ledger and flag anything unclear.",
    proof: "pending rows / duplicates / review"
  },
  {
    label: "Explain the numbers",
    body: "Ask about balances, cash flow, spending, budgets, and runway.",
    proof: "reports / search / budgets"
  },
  {
    label: "Keep things tidy",
    body: "Back up, export, verify, close, and roll back ledger changes.",
    proof: "backup / export / rollback"
  }
];

const dayOneItems = [
  {
    label: "Install",
    command: site.installCommand,
    body: "Install the public npm package for the CLI and MCP server."
  },
  {
    label: "Create a ledger",
    command: "CLOVIS_DB=./ledger.db clovis init",
    body: "Create a local ledger file and choose your currency."
  },
  {
    label: "Plan an import",
    command: "clovis tool refresh_statement --json '{\"action\":\"plan\"}'",
    body: "Preview what is new, what may match, and what needs review."
  },
  {
    label: "Connect an agent",
    command: "CLOVIS_DB=./ledger.db clovis-mcp",
    body: "Point your agent at the local ledger through MCP."
  }
];

const safetyItems = [
  {
    label: "No bank login",
    body: "Work from statement files instead of shared credentials.",
    proof: "QFX / OFX / CSV"
  },
  {
    label: "Local records",
    body: "Keep your ledger on your machine, not in another finance app.",
    proof: "SQLite ledger"
  },
  {
    label: "Preview before commit",
    body: "See what will change before anything is written.",
    proof: `${clovisDocs.stats.dryRunTools} dry-run actions`
  },
  {
    label: "Backups and rollback",
    body: "Keep a recovery path for every serious change.",
    proof: "snapshots / exports"
  }
];

const docsGuides = [
  {
    href: "/docs/getting-started/",
    label: "Start here",
    body: "Install Clovis, create a local ledger, and run the first useful checks without learning the whole surface."
  },
  {
    href: "/docs/agents/",
    label: "Connect an AI agent",
    body: "Install Clovis, point the MCP server at a ledger, and give your agent a bounded bookkeeping toolkit."
  },
  {
    href: "/docs/imports/",
    label: "Import a statement",
    body: "Use QFX, OFX, or CSV files, review pending rows, then commit only after Clovis shows what will change."
  },
  {
    href: "/docs/safety/",
    label: "Ask safely",
    body: "Start with read-only tools, preview writes with dry-run when available, and approve destructive actions explicitly."
  },
  {
    href: "/docs/cli/",
    label: "Use the CLI",
    body: "Browse every command with plain-language guidance, arguments, options, examples, and raw help output."
  },
  {
    href: "/docs/mcp/",
    label: "Find an MCP tool",
    body: "Search the generated MCP catalog by name, category, parameters, return type, or safety profile."
  },
  {
    href: "/docs/schema/",
    label: "Understand the database",
    body: "Browse the SQLite ERD, table families, fields, indexes, triggers, and direct SQL write protocol."
  },
  {
    href: "/docs/changelog/",
    label: "Read release notes",
    body: "Track package changes, compatibility notes, and recent behavior updates from the generated changelog."
  }
];

const docsStaticPages: DocsPageDescriptor[] = [
  {
    kind: "overview",
    path: "/docs/",
    label: "Overview",
    title: "Clovis documentation",
    description:
      "Install Clovis, connect AI agents through MCP, and inspect the CLI and tool surface generated from the public npm package."
  },
  {
    kind: "getting-started",
    path: "/docs/getting-started/",
    label: "Getting started",
    title: "Getting started",
    description:
      "Install Clovis, create a local ledger, import the first statement, and run the first finance checks."
  },
  {
    kind: "agents",
    path: "/docs/agents/",
    label: "Agent setup",
    title: "Connect an AI agent",
    description:
      "Run the Clovis MCP server for Claude Desktop, Cursor, Windsurf, OpenAI-compatible agent runtimes, and other MCP clients."
  },
  {
    kind: "imports",
    path: "/docs/imports/",
    label: "Statement imports",
    title: "Import statements safely",
    description:
      "Bring bank, card, QFX, OFX, and CSV statements into Clovis with review before anything is committed."
  },
  {
    kind: "safety",
    path: "/docs/safety/",
    label: "Safety model",
    title: "Safe agent workflows",
    description:
      "Use read-only tools first, preview writes, require approval for destructive actions, and keep financial data local."
  },
  {
    kind: "files",
    path: "/docs/files/",
    label: "Agent files",
    title: "Machine-readable docs",
    description:
      "JSON and Markdown references for coding assistants, MCP clients, crawlers, and automated documentation consumers."
  },
  {
    kind: "schema",
    path: "/docs/schema/",
    label: "SQLite schema",
    title: "SQLite schema",
    description:
      "Clovis schema v3 explained with the ERD, table families, fields, indexes, triggers, and canonical DDL."
  },
  {
    kind: "changelog",
    path: "/docs/changelog/",
    label: "Changelog",
    title: "Changelog",
    description:
      "Human-readable release notes for the public Clovis npm package, generated from the packaged changelog."
  },
  {
    kind: "cli-index",
    path: "/docs/cli/",
    label: "CLI reference",
    title: "CLI reference",
    description:
      "Every Clovis command with captured help, plain-language descriptions, arguments, options, and examples."
  },
  {
    kind: "mcp-index",
    path: "/docs/mcp/",
    label: "MCP tools",
    title: "MCP tool reference",
    description:
      "Searchable MCP tool catalog with parameters, returns, aliases, safety hints, and agent usage guidance."
  }
];

const cliCommands = clovisDocs.cliCommands as readonly CliCommandItem[];
const mcpTools = clovisDocs.tools as readonly ToolCatalogItem[];
const mcpToolGroups = clovisDocs.toolGroups as readonly ToolCatalogGroup[];
const docsSidebarPages = docsStaticPages.filter((page) => page.kind !== "cli-index" && page.kind !== "mcp-index");
const schemaTables = clovisSchema.tables as readonly SchemaTable[];
const schemaFamilies = clovisSchema.families as readonly SchemaFamily[];
const schemaRelationships = clovisSchema.relationships as readonly SchemaRelationship[];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function matchesToolFilter(tool: ToolCatalogItem, filter: ToolFilter): boolean {
  if (filter === "read-only") return tool.readOnly;
  if (filter === "writes") return !tool.readOnly;
  if (filter === "dry-run") return tool.dryRun;
  if (filter === "destructive") return tool.destructive;
  return true;
}

function cliCommandHref(command: CliCommandItem): string {
  return `/docs/cli/${command.slug}/`;
}

function mcpToolHref(tool: ToolCatalogItem): string {
  return `/docs/mcp/${tool.name}/`;
}

function findCliCommand(slug: string): CliCommandItem | undefined {
  return cliCommands.find((command) => command.slug === slug);
}

function findMcpTool(name: string): ToolCatalogItem | undefined {
  return mcpTools.find((tool) => tool.name === name);
}

function docsPageForPath(path: string): DocsPageDescriptor | undefined {
  const normalizedPath = normalizePath(path);
  const staticPage = docsStaticPages.find((page) => page.path === normalizedPath);

  if (staticPage) {
    return staticPage;
  }

  const cliMatch = normalizedPath.match(/^\/docs\/cli\/([^/]+)\/$/);
  if (cliMatch) {
    const command = findCliCommand(cliMatch[1]);
    if (command) {
      return {
        kind: "cli-command",
        path: normalizedPath,
        label: command.command,
        title: command.command,
        description: command.description || `Help and usage for ${command.command}.`,
        command
      };
    }
  }

  const mcpMatch = normalizedPath.match(/^\/docs\/mcp\/([^/]+)\/$/);
  if (mcpMatch) {
    const tool = findMcpTool(mcpMatch[1]);
    if (tool) {
      return {
        kind: "mcp-tool",
        path: normalizedPath,
        label: tool.name,
        title: tool.name,
        description: `${tool.name} MCP tool reference, parameters, return type, and safety hints.`,
        tool
      };
    }
  }

  return undefined;
}

function routeMetaForPath(path: string) {
  const normalizedPath = normalizePath(path);
  const docsPage = docsPageForPath(normalizedPath);

  if (docsPage) {
    return {
      path: docsPage.path,
      canonicalPath: docsPage.path,
      title: docsPage.kind === "overview" ? "Clovis Documentation" : `${docsPage.title} | Clovis Docs`,
      description: docsPage.description
    };
  }

  return routeForPath(normalizedPath);
}

export function Head({ path }: AppProps) {
  const route = routeMetaForPath(path ?? "/");
  const canonical = `https://${site.domain}${route.canonicalPath ?? route.path}`;

  return (
    <>
      <title>{route.path === "/" ? "Clovis" : `${route.title} | Clovis`}</title>
      <meta name="description" content={route.description} />
      <link rel="canonical" href={canonical} />
      <link rel="preload" href="/assets/fonts/jetbrains-mono-latin.woff2" as="font" type="font/woff2" crossOrigin="" />
      <meta property="og:title" content={route.title} />
      <meta property="og:description" content={route.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
    </>
  );
}

export function App({ initialPath = "/" }: AppProps) {
  const currentPath = normalizePath(initialPath);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <SiteHeader currentPath={currentPath} />
      <main id="main-content">
        <Page path={currentPath} />
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader({ currentPath }: { currentPath: string }) {
  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="Clovis home">
        <img src="/assets/clovis-icon.png" alt="" width="36" height="36" />
        <span>Clovis</span>
      </a>
      <nav className="site-nav" aria-label="Primary navigation">
        {primaryRoutes.map((route) => (
          <a key={route.href} className={currentPath === route.href ? "active" : ""} href={route.href}>
            {route.label}
          </a>
        ))}
      </nav>
    </header>
  );
}

function Page({ path }: { path: string }) {
  const docsPage = docsPageForPath(path);

  if (docsPage) {
    return <DocsPage page={docsPage} />;
  }

  switch (path) {
    case "/pricing/":
    case "/waitlist/":
    case "/download/":
      return <HomePage />;
    case "/changelog/":
      return <DocsPage page={docsStaticPages.find((page) => page.kind === "changelog") ?? docsStaticPages[0]} />;
    case "/terms/":
      return <TermsPage />;
    case "/privacy/":
      return <PrivacyPage />;
    case "/refund-policy/":
      return <RefundPage />;
    case "/support/":
      return <SupportPage />;
    default:
      return <HomePage />;
  }
}

function HomePage() {
  return (
    <>
      <section className="yc-hero">
        <div className="yc-hero-copy">
          <a className="release-pill" href={site.npmUrl}>
            <span>npm</span>
            <strong>clovis@{site.version}</strong>
          </a>
          <h1>Turn your AI agent into your personal CFO.</h1>
          <p>
            Clovis lets your agent review statements, reconcile transactions, and answer money questions against
            records you own. You approve the changes. Your financial data stays on your machine.
          </p>
          <InstallCommand />
        </div>
      </section>

      <section id="install" className="signal-row" aria-label="Product evidence">
        <a href="/docs/mcp/">
          <span>Agent tools</span>
          <strong>{clovisDocs.stats.tools.toLocaleString()} agent actions</strong>
        </a>
        <a href="/docs/cli/">
          <span>Terminal commands</span>
          <strong>{clovisDocs.stats.cliCommands.toLocaleString()} commands</strong>
        </a>
        <div>
          <span>Statement imports</span>
          <strong>QFX, OFX, CSV</strong>
        </div>
        <div>
          <span>Local control</span>
          <strong>{clovisDocs.stats.readOnlyTools.toLocaleString()} read-only actions</strong>
        </div>
      </section>

      <section id="can-do" className="yc-section" aria-labelledby="can-do-title">
        <div className="yc-section-heading">
          <p className="eyebrow">Jobs your agent can take off your plate</p>
          <h2 id="can-do-title">Statement review, reconciliation, and answers you can trace.</h2>
          <p>
            Give your agent the boring, exact parts of bookkeeping: imports, matching, reports, and checks before
            anything changes.
          </p>
        </div>
        <div className="capability-grid">
          {capabilityItems.map((item) => (
            <article key={item.label}>
              <span className="evidence-line">{item.proof}</span>
              <h3>{item.label}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="workflows" className="flow-section" aria-labelledby="flow-title">
        <div>
          <p className="eyebrow">Start small</p>
          <h2 id="flow-title">Try it with one ledger and one statement.</h2>
        </div>
        <div className="flow-list">
          {dayOneItems.map((item) => (
            <article key={item.label}>
              <span>{item.label}</span>
              <code>{item.command}</code>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="trust" className="yc-section" aria-labelledby="safety-title">
        <div className="yc-section-heading">
          <p className="eyebrow">Built for local control</p>
          <h2 id="safety-title">Your agent can do the work without owning the books.</h2>
          <p>
            Clovis works from local source files, previews changes before saving them, and leaves an audit trail you can
            inspect.
          </p>
        </div>
        <div className="capability-grid">
          {safetyItems.map((item) => (
            <article key={item.label}>
              <span className="evidence-line">{item.proof}</span>
              <h3>{item.label}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="final-cta">
        <div>
          <p className="eyebrow">Start with one statement</p>
          <h2>Install Clovis and give your agent real books to work from.</h2>
        </div>
        <div className="button-row">
          <a className="button primary" href={site.npmUrl}>
            <Package size={18} aria-hidden="true" />
            Install Clovis
          </a>
          <a className="button secondary" href={site.githubUrl}>
            <Github size={18} aria-hidden="true" />
            View GitHub
          </a>
        </div>
      </section>
    </>
  );
}

function InstallCommand() {
  const [copied, setCopied] = useState(false);

  async function copyInstallCommand() {
    await navigator.clipboard.writeText(site.installCommand);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="install-command" aria-label="Install command">
      <div className="install-command-copy">
        <span aria-hidden="true">$</span>
        <code>{site.installCommand}</code>
      </div>
      <button type="button" onClick={copyInstallCommand}>
        <Copy size={16} aria-hidden="true" />
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function CopyCodeButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function copyValue() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <button className="docs-copy-button" type="button" onClick={copyValue}>
      <Copy size={14} aria-hidden="true" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function formatDataType(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "type" in value) {
    return String((value as { type: unknown }).type);
  }
  return JSON.stringify(value);
}

function formatDefaultValue(value: unknown): string {
  if (value === undefined) return "";
  if (value === null) return "null";
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}

function parameterPlainDescription(parameter: ToolParameter): string {
  if (parameter.description) return parameter.description;
  if (parameter.name === "dry_run") return "Preview the change without committing it.";
  if (parameter.name.endsWith("_id")) return "Identifier from the local Clovis ledger.";
  if (parameter.name.endsWith("_ids")) return "List of identifiers from the local Clovis ledger.";
  if (parameter.name.includes("date")) return "Date value, usually in YYYY-MM-DD format.";
  if (parameter.name.includes("account")) return "Account selected from the local chart of accounts.";
  if (parameter.name.includes("amount")) return "Money or quantity value in the relevant asset.";
  if (parameter.name.includes("currency") || parameter.name.includes("asset")) return "Currency or asset symbol/id.";
  if (parameter.name.includes("file_path")) return "Path to a local file the user has made available.";
  return "Input used by this tool.";
}

function placeholderForParameter(parameter: ToolParameter): unknown {
  if (parameter.type.includes("boolean")) return parameter.default ?? true;
  if (parameter.type.includes("number")) return parameter.default ?? 100;
  if (parameter.type.includes("string[]")) return [`<${parameter.name}>`];
  if (parameter.type.includes("Record") || parameter.type.includes("unknown")) return {};
  if (parameter.name.includes("date")) return "2026-06-01";
  if (parameter.name.includes("amount")) return 100;
  if (parameter.name.includes("currency")) return "USD";
  if (parameter.name.includes("asset")) return "USD";
  if (parameter.name.includes("file_path")) return "./statement.csv";
  return `<${parameter.name}>`;
}

function commandGroupLabel(command: CliCommandItem): string {
  const root = command.path[0];
  if (!root) return "Core commands";
  if (root === "account") return "Accounts";
  if (root === "txn") return "Transactions";
  if (root === "report") return "Reports";
  if (["init", "import", "export", "balance", "doctor", "tools", "tool"].includes(root)) return "Core commands";
  return root.charAt(0).toUpperCase() + root.slice(1);
}

function groupCliCommands(commands: readonly CliCommandItem[]) {
  const groups = new Map<string, CliCommandItem[]>();

  for (const command of commands) {
    const label = commandGroupLabel(command);
    groups.set(label, [...(groups.get(label) ?? []), command]);
  }

  return [...groups.entries()].map(([label, entries]) => ({ label, commands: entries }));
}

function toolSearchText(tool: ToolCatalogItem, category?: string): string {
  return [
    category,
    tool.name,
    tool.title,
    tool.signature,
    tool.category,
    tool.summary,
    tool.docs.useWhen.join(" "),
    tool.docs.beforeCalling.join(" "),
    tool.parameters.map((parameter) => `${parameter.name} ${parameter.description}`).join(" "),
    Object.keys(tool.aliases).join(" ")
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function safetySummary(tool: ToolCatalogItem): string {
  if (tool.destructive) return "Requires explicit approval. It can remove, void, discard, or permanently change data.";
  if (tool.readOnly) return "Read-only. Good first choice for answering questions and inspecting the ledger.";
  if (tool.dryRun) return "Can write, but supports dry-run. Preview the result before applying it.";
  return "Can write. Ask the user before changing the ledger.";
}

function childCommandFor(parent: CliCommandItem, row: CliHelpRow): CliCommandItem | undefined {
  const childName = row.term.split(/\s+/)[0];
  const path = [...parent.path, childName].join("-");
  return cliCommands.find((command) => command.slug === path);
}

function ToolSafetyPills({ tool }: { tool: ToolCatalogItem }) {
  return (
    <div className="docs-pill-row" aria-label={`${tool.name} safety hints`}>
      <span className={tool.readOnly ? "docs-pill safe" : "docs-pill write"}>
        {tool.readOnly ? "read-only" : "can write"}
      </span>
      {tool.dryRun ? <span className="docs-pill preview">dry-run</span> : null}
      {tool.destructive ? <span className="docs-pill danger">destructive</span> : null}
    </div>
  );
}

function ToolLink({ name }: { name: string }) {
  const tool = findMcpTool(name);
  if (!tool) return <code>{name}</code>;

  return (
    <a className="docs-inline-code-link" href={mcpToolHref(tool)}>
      {name}
    </a>
  );
}

function DocsPage({ page }: { page: DocsPageDescriptor }) {
  return (
    <DocsLayout page={page}>
      {page.kind === "overview" ? <DocsOverviewPage /> : null}
      {page.kind === "getting-started" ? <DocsGettingStartedPage /> : null}
      {page.kind === "agents" ? <DocsAgentsPage /> : null}
      {page.kind === "imports" ? <DocsImportsPage /> : null}
      {page.kind === "safety" ? <DocsSafetyPage /> : null}
      {page.kind === "files" ? <DocsFilesPage /> : null}
      {page.kind === "schema" ? <SchemaPage /> : null}
      {page.kind === "changelog" ? <ChangelogPage /> : null}
      {page.kind === "cli-index" ? <CliIndexPage /> : null}
      {page.kind === "cli-command" && page.command ? <CliCommandPage command={page.command} /> : null}
      {page.kind === "mcp-index" ? <McpIndexPage /> : null}
      {page.kind === "mcp-tool" && page.tool ? <McpToolPage tool={page.tool} /> : null}
    </DocsLayout>
  );
}

function DocsLayout({ page, children }: { page: DocsPageDescriptor; children: ReactNode }) {
  const commandGroups = groupCliCommands(cliCommands);

  return (
    <div className="docs-shell">
      <aside className="docs-sidebar" aria-label="Documentation navigation">
        <a className="docs-sidebar-brand" href="/docs/">
          <img src="/assets/clovis-icon.png" alt="" width="24" height="24" />
          <span>Clovis docs</span>
        </a>
        <div className="docs-sidebar-meta">
          <span>Current package</span>
          <strong>clovis@{clovisDocs.source.version}</strong>
        </div>
        <nav className="docs-sidebar-nav" aria-label="Docs sections">
          {docsSidebarPages.map((item) => (
            <a key={item.path} className={page.path === item.path ? "active" : ""} href={item.path}>
              {item.label}
            </a>
          ))}
        </nav>
        <details className="docs-sidebar-section" open={page.kind.startsWith("cli")}>
          <summary>CLI reference</summary>
          <a className={page.path === "/docs/cli/" ? "active" : ""} href="/docs/cli/">
            All commands
          </a>
          {commandGroups.map((group) => (
            <div className="docs-sidebar-subsection" key={group.label}>
              <span>{group.label}</span>
              {group.commands.map((command) => (
                <a
                  key={command.command}
                  className={page.path === cliCommandHref(command) ? "active" : ""}
                  href={cliCommandHref(command)}
                >
                  {command.command.replace(/^clovis ?/, "") || "clovis"}
                </a>
              ))}
            </div>
          ))}
        </details>
        <details className="docs-sidebar-section" open={page.kind.startsWith("mcp")}>
          <summary>MCP reference</summary>
          <a className={page.path === "/docs/mcp/" ? "active" : ""} href="/docs/mcp/">
            All tools
          </a>
          {mcpToolGroups.map((group) => (
            <details
              className="docs-sidebar-tool-group"
              key={group.category}
              open={page.tool?.category === group.category}
            >
              <summary>{group.category}</summary>
              <div className="docs-sidebar-subsection docs-sidebar-tool-links">
                <a href={`/docs/mcp/#${slugify(group.category)}`}>Category overview</a>
                {group.tools.map((tool) => (
                  <a
                    key={tool.name}
                    className={page.path === mcpToolHref(tool) ? "active" : ""}
                    href={mcpToolHref(tool)}
                  >
                    {tool.name}
                  </a>
                ))}
              </div>
            </details>
          ))}
        </details>
        <div className="docs-sidebar-links">
          <span>Project links</span>
          <a href={site.npmUrl}>npm package</a>
          <a href={site.githubUrl}>GitHub repository</a>
          <a href="/llms.txt">llms.txt</a>
        </div>
      </aside>

      <article className="docs-main">
        <header className="docs-header">
          <div>
            <p className="eyebrow">Documentation</p>
            <h1>{page.title}</h1>
            <p>{page.description}</p>
          </div>
          <a className="button secondary docs-header-action" href={site.npmUrl}>
            <Package size={18} aria-hidden="true" />
            npm
          </a>
        </header>
        {children}
      </article>
    </div>
  );
}

function DocsOverviewPage() {
  const stats = [
    { label: "Package", value: `v${clovisDocs.source.version}` },
    { label: "MCP tools", value: clovisDocs.stats.tools.toLocaleString() },
    { label: "Read-only", value: clovisDocs.stats.readOnlyTools.toLocaleString() },
    { label: "Dry-run ready", value: clovisDocs.stats.dryRunTools.toLocaleString() },
    { label: "Destructive", value: clovisDocs.stats.destructiveTools.toLocaleString() },
    { label: "CLI commands", value: clovisDocs.stats.cliCommands.toLocaleString() }
  ];

  return (
    <>
      <section className="docs-overview" aria-label="Documentation summary">
        {stats.map((item) => (
          <article className="docs-stat" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </article>
        ))}
      </section>

      <section className="docs-reference-section" aria-labelledby="docs-guides-title">
        <div className="docs-section-heading">
          <h2 id="docs-guides-title">Start with the job</h2>
          <p>
            Clovis has two main surfaces: the CLI for humans at a terminal, and MCP tools for AI agents. Pick the job,
            then use the smallest surface that gets it done.
          </p>
        </div>
        <div className="docs-guide-grid">
          {docsGuides.map((guide) => (
            <a className="docs-guide-card" key={guide.href} href={guide.href}>
              <strong>{guide.label}</strong>
              <span>{guide.body}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="docs-mental-model-title">
        <div className="docs-section-heading">
          <h2 id="docs-mental-model-title">The simple mental model</h2>
          <p>
            Clovis keeps a local ledger. You can operate it directly with commands, or let an agent call typed tools
            against that same ledger.
          </p>
        </div>
        <div className="docs-concept-grid">
          <article>
            <strong>CLI</strong>
            <span>Best for direct control: initialize ledgers, import files, run reports, export data, and inspect help.</span>
            <a href="/docs/cli/">Browse commands</a>
          </article>
          <article>
            <strong>MCP</strong>
            <span>Best for agent workflows: ask questions, review imports, reconcile, categorize, and run checks.</span>
            <a href="/docs/mcp/">Browse tools</a>
          </article>
          <article>
            <strong>Manifests</strong>
            <span>Best for automation: exact JSON and Markdown files that coding assistants can ingest.</span>
            <a href="/docs/files/">Open agent files</a>
          </article>
        </div>
      </section>
    </>
  );
}

function DocsGettingStartedPage() {
  return (
    <>
      <section className="docs-reference-section" aria-labelledby="quickstart-title">
        <div className="docs-section-heading">
          <h2 id="quickstart-title">First 10 minutes</h2>
          <p>Install the package, create a ledger, and run one command that proves Clovis can read it.</p>
        </div>
        <div className="docs-step-list">
          {[
            ["Install Clovis", clovisDocs.installCommand, "Installs both the `clovis` CLI and `clovis-mcp` server."],
            ["Create a local ledger", "clovis init --currency USD", "Creates the database and starter accounts."],
            ["Check the ledger", "clovis account balances --format json", "Confirms Clovis can read the ledger."],
            ["Open command help", "clovis --help", "Shows the top-level command tree and global options."]
          ].map(([title, command, body], index) => (
            <article key={title}>
              <span className="docs-step-index">{index + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
                <div className="docs-copy-line">
                  <code>{command}</code>
                  <CopyCodeButton value={command} />
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="what-next-title">
        <div className="docs-section-heading">
          <h2 id="what-next-title">What to do next</h2>
          <p>Use the path that matches how you want to work.</p>
        </div>
        <div className="docs-playbook">
          <article>
            <h3>I want an AI agent to help</h3>
            <p>Connect the MCP server and start with read-only questions before allowing writes.</p>
            <a href="/docs/agents/">Set up an agent</a>
          </article>
          <article>
            <h3>I want to import bank data</h3>
            <p>Use pending imports first so every row can be reviewed before it becomes part of the books.</p>
            <a href="/docs/imports/">Import statements</a>
          </article>
          <article>
            <h3>I want exact command help</h3>
            <p>Open the generated CLI reference. It is rebuilt from the published package help output.</p>
            <a href="/docs/cli/">Browse CLI reference</a>
          </article>
        </div>
      </section>
    </>
  );
}

function DocsAgentsPage() {
  const claudeConfig = `{
  "mcpServers": {
    "clovis": {
      "command": "clovis-mcp",
      "env": {
        "CLOVIS_DB": "/absolute/path/to/ledger.db"
      }
    }
  }
}`;
  const genericConfig = `{
  "mcpServers": {
    "clovis": {
      "command": "clovis-mcp",
      "env": {
        "CLOVIS_DB": "./ledger.db"
      }
    }
  }
}`;
  const stdioCommand = "CLOVIS_DB=./ledger.db clovis-mcp";
  const mcpConfigs = [
    {
      label: "Claude Desktop",
      body: "Add this server entry to the MCP client config and use an absolute ledger path.",
      code: claudeConfig
    },
    {
      label: "Cursor, Windsurf, and other MCP clients",
      body: "Use the same stdio server shape. Keep the ledger path scoped to the project or local workspace.",
      code: genericConfig
    },
    {
      label: "Manual stdio launch",
      body: "Run the server directly when testing an agent connection or checking environment variables.",
      code: stdioCommand
    }
  ];

  return (
    <>
      <section className="docs-reference-section" aria-labelledby="agent-setup-title">
        <div className="docs-section-heading">
          <h2 id="agent-setup-title">Connect through MCP</h2>
          <p>
            Clovis works with MCP-compatible agent clients. That can include Claude Desktop, Cursor, Windsurf, and
            agent runtimes built around Anthropic, OpenAI, or local models.
          </p>
        </div>
        <div className="docs-config-list">
          {mcpConfigs.map((config) => (
            <article className="docs-config-card" key={config.label}>
              <div className="docs-config-card-header">
                <div>
                  <h3>{config.label}</h3>
                  <p>{config.body}</p>
                </div>
                <CopyCodeButton value={config.code} />
              </div>
              <pre className="docs-code-block"><code>{config.code}</code></pre>
            </article>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="agent-asks-title">
        <div className="docs-section-heading">
          <h2 id="agent-asks-title">Good first asks</h2>
          <p>Start with questions that inspect the ledger. Add write actions only after the agent explains the plan.</p>
        </div>
        <div className="docs-example-grid">
          {[
            "Show my account balances and flag anything unusual.",
            "What changed in spending this month compared with last month?",
            "Review pending imports and tell me which rows need a category.",
            "Run integrity checks before making any changes."
          ].map((ask) => (
            <article key={ask}>
              <code>{ask}</code>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function DocsImportsPage() {
  return (
    <>
      <section className="docs-reference-section" aria-labelledby="imports-title">
        <div className="docs-section-heading">
          <h2 id="imports-title">Import with review</h2>
          <p>
            The safest import workflow is: load the statement, keep rows pending, review duplicates and categories,
            then post only what you trust.
          </p>
        </div>
        <div className="docs-playbook">
          <article>
            <h3>1. Prefer QFX or OFX when available</h3>
            <p>Bank-provided statement IDs make duplicate detection and reconciliation more reliable than hand-edited CSV.</p>
            <code>clovis import --file statement.csv --account &lt;checking-id&gt; --counterpart &lt;uncategorized-id&gt;</code>
          </article>
          <article>
            <h3>2. Ask the agent to inspect pending rows</h3>
            <p>
              Useful tools include <ToolLink name="pending_summary" />, <ToolLink name="find_pending_duplicates" />,{" "}
              and <ToolLink name="uncategorized_transactions" />.
            </p>
            <code>Review the pending import and identify duplicates, missing categories, and odd amounts.</code>
          </article>
          <article>
            <h3>3. Commit after reconciliation</h3>
            <p>
              Use dry-run tools when available, keep the original statement, and compare Clovis balances with the
              source account.
            </p>
            <code>clovis account balances --format json</code>
          </article>
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="import-tools-title">
        <div className="docs-section-heading">
          <h2 id="import-tools-title">Key import tools</h2>
          <p>These generated MCP pages are the fastest way to see exact parameters and safety hints.</p>
        </div>
        <div className="docs-related-grid">
          {["import_file", "plan_reconciliation", "apply_reconciliation_plan", "find_pending_duplicates"].map((name) => {
            const tool = findMcpTool(name);
            return tool ? (
              <a key={name} href={mcpToolHref(tool)}>
                <strong>{tool.name}</strong>
                <span>{safetySummary(tool)}</span>
              </a>
            ) : null;
          })}
        </div>
      </section>
    </>
  );
}

function DocsSafetyPage() {
  return (
    <>
      <section className="docs-reference-section" aria-labelledby="safety-title">
        <div className="docs-section-heading">
          <h2 id="safety-title">Agent operating rules</h2>
          <p>Clovis gives agents useful tools, but the user should stay in control of financial records.</p>
        </div>
        <div className="docs-safety-grid">
          <article>
            <strong>Read first</strong>
            <span>Use balances, reports, search, pending import review, and integrity checks before mutation.</span>
          </article>
          <article>
            <strong>Preview writes</strong>
            <span>When a tool supports dry-run, require the agent to show the preview before applying changes.</span>
          </article>
          <article>
            <strong>Approve destructive tools</strong>
            <span>Deletes, rollbacks, branch discards, and repairs should be explicit user-approved actions.</span>
          </article>
          <article>
            <strong>Keep data local</strong>
            <span>Clovis reads the ledger path supplied by <code>CLOVIS_DB</code>; do not send raw files unless needed.</span>
          </article>
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="safety-flags-title">
        <div className="docs-section-heading">
          <h2 id="safety-flags-title">How to read safety flags</h2>
          <p>The generated MCP catalog marks tools by how an agent should treat them.</p>
        </div>
        <div className="docs-concept-grid">
          <article>
            <strong>read-only</strong>
            <span>Safe first step for questions, reports, searches, and diagnostics.</span>
            <a href="/docs/mcp/">Open tool catalog</a>
          </article>
          <article>
            <strong>dry-run</strong>
            <span>Can preview a write. Ask the agent to run the preview, explain it, then wait.</span>
            <a href="/docs/mcp/">Open tool catalog</a>
          </article>
          <article>
            <strong>destructive</strong>
            <span>Can remove, void, discard, or repair records. Require explicit approval.</span>
            <a href="/docs/mcp/">Open tool catalog</a>
          </article>
        </div>
      </section>
    </>
  );
}

function DocsFilesPage() {
  return (
    <section className="docs-reference-section" aria-labelledby="docs-files-title">
      <div className="docs-section-heading">
        <h2 id="docs-files-title">Machine-readable files</h2>
        <p>Use these when configuring agents, building docs, or checking exact tool and command metadata.</p>
      </div>
      <div className="docs-resource-list">
        {clovisDocs.agentFiles.map((file) => (
          <a className="docs-resource-row" key={file.href} href={file.href}>
            <FileJson size={18} aria-hidden="true" />
            <span>
              <strong>{file.label}</strong>
              <small>{file.description}</small>
            </span>
            <ExternalLink size={15} aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>
  );
}

function CliIndexPage() {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const commands = cliCommands.filter((command) =>
    `${command.command} ${command.description} ${command.usage}`.toLowerCase().includes(normalizedQuery)
  );
  const groups = groupCliCommands(commands);

  return (
    <section className="docs-reference-section" aria-labelledby="cli-index-title">
      <div className="docs-section-heading">
        <h2 id="cli-index-title">Commands</h2>
        <p>Every command is generated from <code>--help</code>, then wrapped with plain-language notes and deep links.</p>
      </div>
      <div className="docs-tool-controls" aria-label="CLI command search">
        <label htmlFor="cli-search">Search commands</label>
        <input
          id="cli-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder="Search by command, option, or description"
        />
        <p>
          Showing {commands.length.toLocaleString()} of {cliCommands.length.toLocaleString()} commands.
        </p>
      </div>
      <div className="docs-command-groups">
        {groups.map((group) => (
          <section className="docs-command-group" key={group.label}>
            <h3>{group.label}</h3>
            <div className="docs-command-list">
              {group.commands.map((command) => (
                <article key={command.command}>
                  <code>
                    <a href={cliCommandHref(command)}>{command.command}</a>
                  </code>
                  <span>{command.description}</span>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function CliCommandPage({ command }: { command: CliCommandItem }) {
  return (
    <>
      <section className="docs-reference-section" aria-labelledby="cli-command-title">
        <div className="docs-section-heading">
          <h2 id="cli-command-title">What it does</h2>
          <p>{command.description || "Runs a Clovis CLI workflow against the selected local ledger."}</p>
        </div>
        <div className="docs-command-hero">
          <div>
            <span>Usage</span>
            <code>{command.usage}</code>
          </div>
          <CopyCodeButton value={command.usage.replace(/^Usage: /, "")} />
        </div>
      </section>

      <HelpRows title="Arguments" rows={command.sections.arguments} />
      <HelpRows title="Options" rows={command.sections.options} />
      <CommandRows title="Subcommands" command={command} rows={command.sections.commands} />
      <TextRows title="Examples" rows={command.sections.examples} code />
      <TextRows title="Notes" rows={command.sections.notes} />

      <section className="docs-reference-section" aria-labelledby="raw-help-title">
        <div className="docs-section-heading">
          <h2 id="raw-help-title">Raw help output</h2>
          <p>This is the exact help text captured from the published package.</p>
        </div>
        <pre className="docs-code-block"><code>{command.help}</code></pre>
      </section>
    </>
  );
}

function HelpRows({ title, rows }: { title: string; rows: readonly CliHelpRow[] }) {
  if (rows.length === 0) return null;

  return (
    <section className="docs-reference-section" aria-labelledby={`${slugify(title)}-title`}>
      <div className="docs-section-heading">
        <h2 id={`${slugify(title)}-title`}>{title}</h2>
      </div>
      <div className="docs-detail-table">
        {rows.map((row) => (
          <article key={row.raw}>
            <code>{row.term}</code>
            <span>{row.description || "No description provided by the CLI help output."}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function CommandRows({ title, command, rows }: { title: string; command: CliCommandItem; rows: readonly CliHelpRow[] }) {
  if (rows.length === 0) return null;

  return (
    <section className="docs-reference-section" aria-labelledby={`${slugify(title)}-title`}>
      <div className="docs-section-heading">
        <h2 id={`${slugify(title)}-title`}>{title}</h2>
      </div>
      <div className="docs-detail-table">
        {rows.map((row) => {
          const child = childCommandFor(command, row);
          return (
            <article key={row.raw}>
              <code>{child ? <a href={cliCommandHref(child)}>{row.term}</a> : row.term}</code>
              <span>{row.description || "No description provided by the CLI help output."}</span>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function TextRows({ title, rows, code = false }: { title: string; rows: readonly string[]; code?: boolean }) {
  if (rows.length === 0) return null;

  return (
    <section className="docs-reference-section" aria-labelledby={`${slugify(title)}-title`}>
      <div className="docs-section-heading">
        <h2 id={`${slugify(title)}-title`}>{title}</h2>
      </div>
      <div className={code ? "docs-example-grid" : "docs-note-list"}>
        {rows.map((row) => (
          <article key={row}>{code ? <code>{row.trim()}</code> : <span>{row}</span>}</article>
        ))}
      </div>
    </section>
  );
}

function McpIndexPage() {
  const [toolQuery, setToolQuery] = useState("");
  const [toolFilter, setToolFilter] = useState<ToolFilter>("all");
  const allTools = [...mcpTools];
  const normalizedToolQuery = toolQuery.trim().toLowerCase();
  const filteredToolGroups: ToolCatalogGroup[] = mcpToolGroups
    .map((group) => ({
      category: group.category,
      tools: [...group.tools].filter((tool) => {
        return toolSearchText(tool, group.category).includes(normalizedToolQuery) && matchesToolFilter(tool, toolFilter);
      })
    }))
    .filter((group) => group.tools.length > 0);
  const visibleToolCount = filteredToolGroups.reduce((count, group) => count + group.tools.length, 0);
  const toolFilterOptions: Array<{ id: ToolFilter; label: string; count: number }> = [
    { id: "all", label: "All", count: allTools.length },
    { id: "read-only", label: "Read-only", count: allTools.filter((tool) => tool.readOnly).length },
    { id: "writes", label: "Can write", count: allTools.filter((tool) => !tool.readOnly).length },
    { id: "dry-run", label: "Dry-run", count: allTools.filter((tool) => tool.dryRun).length },
    { id: "destructive", label: "Destructive", count: allTools.filter((tool) => tool.destructive).length }
  ];

  return (
    <section className="docs-reference-section" aria-labelledby="mcp-tools-title">
      <div className="docs-section-heading">
        <h2 id="mcp-tools-title">Search tools</h2>
        <p>
          Read-only tools are best for answering questions. Dry-run tools should be previewed before writes. Destructive
          tools should require explicit approval.
        </p>
      </div>
      <div className="docs-tool-controls" aria-label="MCP tool filters">
        <label htmlFor="tool-search">Search tools</label>
        <input
          id="tool-search"
          type="search"
          value={toolQuery}
          onChange={(event) => setToolQuery(event.currentTarget.value)}
          placeholder="Search by name, category, parameter, or signature"
        />
        <div className="docs-filter-row" role="group" aria-label="Filter tools by safety profile">
          {toolFilterOptions.map((option) => (
            <button
              key={option.id}
              className={toolFilter === option.id ? "active" : ""}
              type="button"
              onClick={() => setToolFilter(option.id)}
            >
              <span>{option.label}</span>
              <strong>{option.count}</strong>
            </button>
          ))}
        </div>
        <p>
          Showing {visibleToolCount.toLocaleString()} of {allTools.length.toLocaleString()} tools.
        </p>
      </div>
      <div className="docs-tool-groups">
        {filteredToolGroups.map((group, groupIndex) => (
          <details
            className="docs-tool-group"
            id={slugify(group.category)}
            key={group.category}
            open={normalizedToolQuery !== "" || toolFilter !== "all" || groupIndex === 0}
          >
            <summary>
              <span>{group.category}</span>
              <strong>{group.tools.length} tools</strong>
            </summary>
            <div className="docs-tool-table">
              {group.tools.map((tool) => (
                <article className="docs-tool-row" id={`tool-${tool.name}`} key={tool.name}>
                  <div>
                    <h3>
                      <a href={mcpToolHref(tool)}>{tool.name}</a>
                    </h3>
                    <p>{tool.summary}</p>
                    <code>{tool.signature}</code>
                  </div>
                  <ToolSafetyPills tool={tool} />
                </article>
              ))}
            </div>
          </details>
        ))}
        {filteredToolGroups.length === 0 ? (
          <p className="docs-empty-state">No tools match the current search and filter.</p>
        ) : null}
      </div>
    </section>
  );
}

function McpToolPage({ tool }: { tool: ToolCatalogItem }) {
  const exampleArguments =
    Object.keys(tool.docs.exampleArguments).length > 0
      ? tool.docs.exampleArguments
      : Object.fromEntries(
          tool.parameters
            .filter((parameter) => parameter.required)
            .slice(0, 8)
            .map((parameter) => [parameter.name, placeholderForParameter(parameter)])
        );
  const relatedTools = mcpTools
    .filter((candidate) => candidate.category === tool.category && candidate.name !== tool.name)
    .slice(0, 6);

  return (
    <>
      <section className="docs-reference-section" aria-labelledby="tool-overview-title">
        <div className="docs-section-heading">
          <h2 id="tool-overview-title">What it does</h2>
          <p>{tool.docs.summary}</p>
        </div>
        <div className="docs-tool-detail-hero">
          <div>
            <span>Signature</span>
            <code>{tool.signature}</code>
          </div>
          <ToolSafetyPills tool={tool} />
        </div>
        <p className="docs-callout">{tool.docs.safety}</p>
      </section>

      <section className="docs-reference-section" aria-labelledby="tool-use-title">
        <div className="docs-section-heading">
          <h2 id="tool-use-title">When to use it</h2>
          <p>Use this section to decide whether the tool fits the user request before calling it.</p>
        </div>
        <div className="docs-note-list">
          {tool.docs.useWhen.map((item) => (
            <article key={item}>
              <span>{item}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="tool-before-title">
        <div className="docs-section-heading">
          <h2 id="tool-before-title">Before calling</h2>
          <p>Agents should gather these facts or confirmations first.</p>
        </div>
        <div className="docs-check-list">
          {tool.docs.beforeCalling.map((item, index) => (
            <article key={item}>
              <span>{index + 1}</span>
              <p>{item}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="tool-parameters-title">
        <div className="docs-section-heading">
          <h2 id="tool-parameters-title">Parameters</h2>
          <p>Required inputs are marked clearly. Optional inputs can usually be omitted unless the workflow needs them.</p>
        </div>
        {tool.parameters.length > 0 ? (
          <div className="docs-parameter-table">
            {tool.parameters.map((parameter) => (
              <article key={parameter.name}>
                <div>
                  <code>{parameter.name}</code>
                  <span>{parameter.required ? "required" : "optional"}</span>
                </div>
                <strong>{parameter.type}{parameter.nullable ? " | null" : ""}</strong>
                <p>{parameterPlainDescription(parameter)}</p>
                {parameter.default !== undefined ? <small>Default: {formatDefaultValue(parameter.default)}</small> : null}
              </article>
            ))}
          </div>
        ) : (
          <p className="docs-empty-state">This tool does not take parameters.</p>
        )}
      </section>

      <section className="docs-reference-section" aria-labelledby="tool-call-title">
        <div className="docs-section-heading">
          <h2 id="tool-call-title">Agent call shape</h2>
          <p>Use the exact tool name and pass arguments as structured JSON through the MCP client.</p>
        </div>
        <article className="docs-config-card">
          <div className="docs-config-card-header">
            <div>
              <h3>{tool.name}</h3>
              <p>Copy this shape into agent instructions, evals, or MCP client tests.</p>
            </div>
            <CopyCodeButton value={JSON.stringify({ tool: tool.name, arguments: exampleArguments }, null, 2)} />
          </div>
          <pre className="docs-code-block"><code>{JSON.stringify({ tool: tool.name, arguments: exampleArguments }, null, 2)}</code></pre>
        </article>
      </section>

      <section className="docs-reference-section" aria-labelledby="tool-metadata-title">
        <div className="docs-section-heading">
          <h2 id="tool-metadata-title">Metadata</h2>
        </div>
        <div className="docs-metadata-grid">
          <article>
            <span>Category</span>
            <strong>{tool.category}</strong>
          </article>
          <article>
            <span>Returns</span>
            <strong>{formatDataType(tool.returns)}</strong>
            <small>{tool.docs.returns}</small>
          </article>
          <article>
            <span>Aliases</span>
            <strong>{Object.keys(tool.aliases).length ? Object.entries(tool.aliases).map(([key, value]) => `${key} -> ${String(value)}`).join(", ") : "None"}</strong>
          </article>
        </div>
      </section>

      {relatedTools.length ? (
        <section className="docs-reference-section" aria-labelledby="related-tools-title">
          <div className="docs-section-heading">
            <h2 id="related-tools-title">Related tools</h2>
          </div>
          <div className="docs-related-grid">
            {relatedTools.map((related) => (
              <a key={related.name} href={mcpToolHref(related)}>
                <strong>{related.name}</strong>
                <span>{safetySummary(related)}</span>
              </a>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

const schemaCoreFlow = [
  {
    table: "books",
    note: "workspace",
    summary: "One database can hold multiple books. Every account, journal, and line is scoped back to one book.",
  },
  {
    table: "accounts",
    note: "posting buckets",
    summary: "The buckets for cash, cards, liabilities, income, expenses, equity, and anything else you report on.",
  },
  {
    table: "journals",
    note: "transaction headers",
    summary: "The dated event: description, source, status, import context, and the lock point for posting.",
  },
  {
    table: "journal_lines",
    note: "balanced facts",
    summary: "The debits and credits. Finalized journals need lines, and every posted journal must balance to zero.",
  },
  {
    table: "assets",
    note: "units",
    summary: "The currency, security, or unit attached to each line, price, lot, and conversion.",
  },
] as const;

function schemaTable(name: string): SchemaTable {
  const table = schemaTables.find((candidate) => candidate.name === name);
  if (!table) throw new Error(`Missing schema table ${name}`);
  return table;
}

function columnRules(column: SchemaColumn): string[] {
  const rules = [];
  if (column.primaryKey) rules.push("primary key");
  if (column.notNull) rules.push("not null");
  if (column.unique) rules.push("unique");
  if (column.references) rules.push(`references ${column.references.table}`);
  if (column.default) rules.push(`default ${column.default}`);
  if (column.check) rules.push("check");
  return rules;
}

type ErdViewId = "core" | "imports" | "planning" | "investments" | "full";

type ErdView = {
  id: ErdViewId;
  label: string;
  summary: string;
  tables: readonly string[];
};

type ErdLayoutNode = {
  table: SchemaTable;
  x: number;
  y: number;
  width: number;
  height: number;
};

type ErdLayoutEdge = {
  relationship: SchemaRelationship;
  sections: readonly ElkEdgeSection[];
};

type ErdLayout = {
  width: number;
  height: number;
  nodes: readonly ErdLayoutNode[];
  edges: readonly ErdLayoutEdge[];
};

const erdNodeSize = {
  width: 180,
  height: 168,
};

const erdSvgPadding = 32;

let schemaElkPromise: Promise<ELK> | null = null;

async function schemaElkLayout(graph: ElkNode): Promise<ElkNode> {
  schemaElkPromise ??= import("elkjs/lib/elk.bundled.js").then(({ default: ELKConstructor }) => new ELKConstructor());
  return schemaElkPromise.then((elk) => elk.layout(graph));
}

const erdViews: readonly ErdView[] = [
  {
    id: "core",
    label: "Core",
    summary: "The durable accounting path: books, accounts, journals, lines, assets, and sources.",
    tables: ["books", "assets", "accounts", "journals", "journal_lines", "sources"],
  },
  {
    id: "imports",
    label: "Imports",
    summary: "Statement import planning, source batches, matched journals, and created rows.",
    tables: ["books", "assets", "accounts", "sources", "journals", "statement_plans", "statement_plan_rows"],
  },
  {
    id: "planning",
    label: "Planning",
    summary: "Budgets, goals, scheduled transfers, and accounting period closes.",
    tables: ["books", "assets", "accounts", "targets", "recurrences", "period_closes"],
  },
  {
    id: "investments",
    label: "Investments",
    summary: "Asset prices, holdings lots, accounts, and the journals that open or close lots.",
    tables: ["books", "assets", "accounts", "journals", "prices", "lots"],
  },
  {
    id: "full",
    label: "Full schema",
    summary: "Every generated table and relationship in the current SQLite schema.",
    tables: schemaTables.map((table) => table.name),
  },
] as const;

function relationshipColumnText(columns: readonly string[]): string {
  return columns.join(", ");
}

function relationshipLabel(relationship: SchemaRelationship): string {
  return `${relationship.fromTable}.${relationshipColumnText(relationship.fromColumns)} -> ${relationship.toTable}.${relationshipColumnText(relationship.toColumns)}`;
}

function relationshipTone(relationship: SchemaRelationship): string {
  const coreTables = new Set(["books", "assets", "accounts", "journals", "journal_lines"]);
  if (coreTables.has(relationship.fromTable) && coreTables.has(relationship.toTable)) return "core";
  if (relationship.onDelete === "cascade") return "cascade";
  if (relationship.optional) return "optional";
  return "standard";
}

function erdRelationshipsForView(view: ErdView): readonly SchemaRelationship[] {
  const tableNames = new Set(view.tables);
  return schemaRelationships.filter((relationship) => {
    if (view.id !== "full" && relationship.fromTable === relationship.toTable) return false;
    if (
      view.id !== "full"
      && relationship.toTable === "books"
      && relationship.fromTable !== "accounts"
      && relationship.fromColumns.length === 1
      && relationship.fromColumns[0] === "book_id"
    ) {
      return false;
    }

    return tableNames.has(relationship.fromTable) && tableNames.has(relationship.toTable);
  });
}

function erdTablesForView(view: ErdView): readonly SchemaTable[] {
  const tableNames = new Set(view.tables);
  return schemaTables.filter((table) => tableNames.has(table.name));
}

function erdBuildGraph(
  view: ErdView,
  tables: readonly SchemaTable[],
  relationships: readonly SchemaRelationship[],
): ElkNode {
  return {
    id: `schema-${view.id}`,
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.padding": `[top=${erdSvgPadding},left=${erdSvgPadding},bottom=${erdSvgPadding},right=${erdSvgPadding}]`,
      "elk.spacing.nodeNode": "44",
      "elk.layered.spacing.nodeNodeBetweenLayers": "48",
      "elk.layered.spacing.edgeEdgeBetweenLayers": "16",
      "elk.layered.spacing.edgeNodeBetweenLayers": "26",
      "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
      "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
    },
    children: tables.map((table) => ({
      id: table.name,
      width: erdNodeSize.width,
      height: erdNodeSize.height,
    })),
    edges: relationships.map((relationship) => ({
      id: relationship.id,
      sources: [relationship.toTable],
      targets: [relationship.fromTable],
    })),
  };
}

function erdLayoutFromElk(
  graph: ElkNode,
  tables: readonly SchemaTable[],
  relationships: readonly SchemaRelationship[],
): ErdLayout {
  const tableByName = new Map<string, SchemaTable>(tables.map((table) => [table.name, table]));
  const relationshipById = new Map<string, SchemaRelationship>(relationships.map((relationship) => [relationship.id, relationship]));
  const nodes = (graph.children ?? []).flatMap((node): ErdLayoutNode[] => {
    const table = tableByName.get(node.id);
    if (!table) return [];
    return [{
      table,
      x: node.x ?? 0,
      y: node.y ?? 0,
      width: node.width ?? erdNodeSize.width,
      height: node.height ?? erdNodeSize.height,
    }];
  });

  const edges = ((graph.edges ?? []) as ElkExtendedEdge[]).flatMap((edge): ErdLayoutEdge[] => {
    const relationship = edge.id ? relationshipById.get(edge.id) : undefined;
    if (!relationship || !edge.sections?.length) return [];
    return [{
      relationship,
      sections: edge.sections,
    }];
  });

  return {
    width: Math.ceil(graph.width ?? erdNodeSize.width),
    height: Math.ceil(graph.height ?? erdNodeSize.height),
    nodes,
    edges,
  };
}

function erdRound(value: number): number {
  return Math.round(value * 10) / 10;
}

function erdSectionPath(section: ElkEdgeSection): string {
  const points = [section.startPoint, ...(section.bendPoints ?? []), section.endPoint];
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${erdRound(point.x)} ${erdRound(point.y)}`)
    .join(" ");
}

function erdTruncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1))}…`;
}

function erdTitleLines(value: string): readonly string[] {
  if (value.length <= 19) return [value];
  const parts = value.split("_");
  if (parts.length <= 1) return [erdTruncate(value, 21)];

  const lines: string[] = [];
  let current = "";
  for (const part of parts) {
    const candidate = current ? `${current}_${part}` : part;
    if (candidate.length <= 18) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = part;
    }
  }

  if (current) lines.push(current);
  if (lines.length <= 2) return lines;
  return [lines[0], erdTruncate(lines.slice(1).join("_"), 21)];
}

function erdTableColumns(table: SchemaTable): readonly SchemaColumn[] {
  const priority = table.columns.filter((column) =>
    column.primaryKey
    || Boolean(column.references)
    || ["book_id", "account_id", "journal_id", "asset_id", "source_id", "plan_id"].includes(column.name),
  );
  const fallback = table.columns.filter((column) => !priority.includes(column));
  return [...priority, ...fallback].slice(0, 3);
}

function erdColumnBadge(column: SchemaColumn): string {
  if (column.primaryKey) return "PK";
  if (column.references) return "FK";
  return column.type;
}

function SchemaErdSvgNode({ node }: { node: ErdLayoutNode }) {
  const titleLines = erdTitleLines(node.table.name);
  const shownColumns = erdTableColumns(node.table);
  const extraColumns = node.table.columns.length - shownColumns.length;
  const rows = [
    ...shownColumns.map((column) => ({
      key: column.name,
      name: column.name,
      badge: erdColumnBadge(column),
    })),
    ...(extraColumns > 0 ? [{
      key: "__extra",
      name: `+${extraColumns} fields`,
      badge: "more",
    }] : []),
  ];

  return (
    <g className={`schema-erd-svg-node family-${slugify(node.table.family)}`} transform={`translate(${erdRound(node.x)} ${erdRound(node.y)})`}>
      <title>{node.table.name}</title>
      <rect className="schema-erd-node-card" height={node.height} rx="8" width={node.width} />
      <rect className="schema-erd-node-band" height="4" rx="2" width={node.width} />
      <line className="schema-erd-node-divider" x1="0" x2={node.width} y1="62" y2="62" />
      <text className="schema-erd-node-family" x="12" y="22">{erdTruncate(node.table.family, 24)}</text>
      <text className="schema-erd-node-title" x="12" y={titleLines.length > 1 ? 39 : 44}>
        {titleLines.map((line, index) => (
          <tspan key={`${line}-${index}`} x="12" dy={index === 0 ? 0 : 15}>{line}</tspan>
        ))}
      </text>
      {rows.map((row, index) => {
        const y = 86 + index * 23;
        return (
          <g className="schema-erd-node-row" key={row.key}>
            <text className="schema-erd-node-field" x="12" y={y}>{erdTruncate(row.name, 21)}</text>
            <rect className="schema-erd-node-badge" height="18" rx="9" width="36" x={node.width - 48} y={y - 13} />
            <text className="schema-erd-node-badge-text" textAnchor="middle" x={node.width - 30} y={y + 1}>{row.badge}</text>
          </g>
        );
      })}
    </g>
  );
}

function SchemaErd() {
  const [activeViewId, setActiveViewId] = useState<ErdViewId>("core");
  const activeView = erdViews.find((view) => view.id === activeViewId) ?? erdViews[0];
  const visibleRelationships = useMemo(() => erdRelationshipsForView(activeView), [activeView]);
  const visibleTables = useMemo(() => erdTablesForView(activeView), [activeView]);
  const [layout, setLayout] = useState<ErdLayout | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const compositeCount = visibleRelationships.filter((relationship) => relationship.composite).length;
  const cascadeCount = visibleRelationships.filter((relationship) => relationship.onDelete === "cascade").length;

  useEffect(() => {
    let cancelled = false;
    setLayout(null);
    setLayoutError(null);

    schemaElkLayout(erdBuildGraph(activeView, visibleTables, visibleRelationships))
      .then((graph) => {
        if (!cancelled) setLayout(erdLayoutFromElk(graph, visibleTables, visibleRelationships));
      })
      .catch((error: unknown) => {
        if (!cancelled) setLayoutError(error instanceof Error ? error.message : "Unable to render schema diagram.");
      });

    return () => {
      cancelled = true;
    };
  }, [activeView, visibleRelationships, visibleTables]);

  return (
    <div className="schema-erd">
      <div className="schema-erd-tabs" role="tablist" aria-label="ERD views">
        {erdViews.map((view) => (
          <button
            aria-selected={activeView.id === view.id}
            className={activeView.id === view.id ? "active" : ""}
            key={view.id}
            onClick={() => setActiveViewId(view.id)}
            role="tab"
            type="button"
          >
            {view.label}
          </button>
        ))}
      </div>

      <div className="schema-erd-toolbar">
        <article>
          <span>Relationships</span>
          <strong>{visibleRelationships.length}</strong>
        </article>
        <article>
          <span>Composite keys</span>
          <strong>{compositeCount}</strong>
        </article>
        <article>
          <span>Cascade links</span>
          <strong>{cascadeCount}</strong>
        </article>
        <div className="schema-erd-legend" aria-label="ERD legend">
          <span><i className="schema-erd-swatch core" /> core path</span>
          <span><i className="schema-erd-swatch optional" /> optional</span>
          <span><i className="schema-erd-swatch cascade" /> cascade</span>
        </div>
      </div>

      <p className="schema-erd-summary">{activeView.summary}</p>

      <div className="schema-erd-shell" role="img" aria-label={`${activeView.label} entity relationship diagram`}>
        {layoutError ? (
          <div className="schema-erd-loading" role="status">{layoutError}</div>
        ) : layout ? (
          <svg
            aria-hidden="true"
            className="schema-erd-svg"
            height={layout.height}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width={layout.width}
          >
            <g className="schema-erd-edge-layer">
              {layout.edges.map((edge) => edge.sections.map((section) => (
                <path
                  className={`schema-erd-edge ${relationshipTone(edge.relationship)}`}
                  d={erdSectionPath(section)}
                  key={`${edge.relationship.id}-${section.id}`}
                />
              )))}
            </g>
            <g className="schema-erd-node-layer">
              {layout.nodes.map((node) => (
                <SchemaErdSvgNode key={node.table.name} node={node} />
              ))}
            </g>
          </svg>
        ) : (
          <div className="schema-erd-loading" role="status">Preparing generated schema diagram...</div>
        )}
      </div>

      <div className="schema-relationship-list" aria-label={`${activeView.label} schema relationships`}>
        {visibleRelationships.map((relationship) => (
          <a href={`#table-${relationship.fromTable}`} key={relationship.id}>
            <code>{relationshipLabel(relationship)}</code>
            <span>
              {relationship.required ? "required" : "optional"}
              {relationship.composite ? " composite" : ""}
              {relationship.onDelete ? `, on delete ${relationship.onDelete}` : ""}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

function SchemaPage() {
  const coreTables = schemaCoreFlow.map((item) => schemaTable(item.table));
  const totalColumns = schemaTables.reduce((sum, table) => sum + table.columns.length, 0);

  return (
    <>
      <section className="schema-hero-panel" aria-labelledby="schema-model-title">
        <div>
          <p className="eyebrow">Schema v{clovisSchema.schemaVersion}</p>
          <h2 id="schema-model-title">Facts first. Balances later.</h2>
          <p>{clovisSchema.model}</p>
        </div>
        <div className="schema-metrics" aria-label="Schema summary">
          <article>
            <span>Tables</span>
            <strong>{schemaTables.length}</strong>
          </article>
          <article>
            <span>Fields</span>
            <strong>{totalColumns}</strong>
          </article>
          <article>
            <span>Indexes</span>
            <strong>{clovisSchema.indexes.length}</strong>
          </article>
          <article>
            <span>Triggers</span>
            <strong>{clovisSchema.triggers.length}</strong>
          </article>
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="schema-core-title">
        <div className="docs-section-heading">
          <h2 id="schema-core-title">Core ledger path</h2>
          <p>
            The heart of Clovis is small: transaction headers in <code>journals</code>, balanced legs in{" "}
            <code>journal_lines</code>, buckets in <code>accounts</code>, and units in <code>assets</code>.
          </p>
        </div>
        <div className="schema-flow" aria-label="Core schema flow">
          {coreTables.map((table, index) => (
            <article key={table.name}>
              <span>{index + 1}</span>
              <strong>{table.name}</strong>
              <small>{schemaCoreFlow[index].note}</small>
              <p>{schemaCoreFlow[index].summary}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="schema-map-title">
        <div className="docs-section-heading">
          <h2 id="schema-map-title">Generated ERD</h2>
          <p>Foreign keys are parsed from the current SQLite DDL, then drawn as one-to-many relationships.</p>
        </div>
        <SchemaErd />
        <div className="schema-family-grid">
          {schemaFamilies.map((family) => (
            <article className="schema-family-card" key={family.name}>
              <span>{family.name}</span>
              <p>{family.summary}</p>
              <div>
                {family.tables.map((table) => (
                  <a key={table} href={`#table-${table}`}>
                    {table}
                  </a>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="schema-rules-title">
        <div className="docs-section-heading">
          <h2 id="schema-rules-title">Rules that protect the ledger</h2>
          <p>SQLite handles durable invariants; the TypeScript engine handles higher-level accounting policy.</p>
        </div>
        <div className="schema-rule-grid">
          <article>
            <span>Per-asset balance</span>
            <strong>Finalized journals must sum to zero for every asset.</strong>
          </article>
          <article>
            <span>Draft then finalize</span>
            <strong>Transactions are inserted as drafts, given lines, then finalized.</strong>
          </article>
          <article>
            <span>Immutable facts</span>
            <strong>Finalized journal lines and reviewed statement rows cannot be casually rewritten.</strong>
          </article>
          <article>
            <span>Closed periods</span>
            <strong>Period closes block old journal finalization and reopening until explicitly reopened.</strong>
          </article>
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="schema-fields-title">
        <div className="docs-section-heading">
          <h2 id="schema-fields-title">Tables and fields</h2>
          <p>Exact columns come from the canonical DDL. Descriptions come from the package schema explainer.</p>
        </div>
        <div className="schema-table-list">
          {schemaTables.map((table) => (
            <details className="schema-table-card" id={`table-${table.name}`} key={table.name} open={table.family === "Accounting core"}>
              <summary>
                <span>{table.family}</span>
                <strong>{table.name}</strong>
                <small>{table.columns.length} fields</small>
              </summary>
              <p>{table.summary}</p>
              <div className="schema-field-table">
                {table.columns.map((column) => (
                  <article key={column.name}>
                    <code>{column.name}</code>
                    <span>{column.type}</span>
                    <div>
                      {columnRules(column).map((rule) => (
                        <small key={rule}>{rule}</small>
                      ))}
                    </div>
                    <p>{column.description}</p>
                  </article>
                ))}
              </div>
              {table.constraints.length ? (
                <div className="schema-constraint-list">
                  <span>Table constraints</span>
                  {table.constraints.map((constraint) => (
                    <code key={constraint}>{constraint}</code>
                  ))}
                </div>
              ) : null}
            </details>
          ))}
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="schema-indexes-title">
        <div className="docs-section-heading">
          <h2 id="schema-indexes-title">Indexes and triggers</h2>
          <p>Indexes keep common ledger scans fast. Triggers protect facts even when someone writes SQL directly.</p>
        </div>
        <div className="schema-ops-grid">
          <article>
            <h3>Indexes</h3>
            {clovisSchema.indexes.map((index) => (
              <div key={index.name}>
                <code>{index.name}</code>
                <span>{index.table} ({index.columns}){index.where ? ` where ${index.where}` : ""}</span>
              </div>
            ))}
          </article>
          <article>
            <h3>Triggers</h3>
            {clovisSchema.triggers.map((trigger) => (
              <div key={trigger.name}>
                <code>{trigger.name}</code>
                <span>{trigger.summary}</span>
              </div>
            ))}
          </article>
        </div>
      </section>

      <section className="docs-reference-section" aria-labelledby="schema-ddl-title">
        <div className="docs-section-heading">
          <h2 id="schema-ddl-title">Canonical DDL</h2>
          <p>
            This page is generated from <code>src/core/schema.ts</code>. The JSON version is available at{" "}
            <a href="/docs/clovis-schema.json">/docs/clovis-schema.json</a>.
          </p>
        </div>
        <details className="schema-ddl">
          <summary>Show full SQLite DDL</summary>
          <pre className="docs-code-block"><code>{clovisSchema.ddl}</code></pre>
        </details>
      </section>
    </>
  );
}

function formatReleaseDate(date: string): string {
  if (!date) return "Date unavailable";

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}

function InlineCodeText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`)/g).map((part, index) => {
        if (part.startsWith("`") && part.endsWith("`")) {
          return <code key={`${part}-${index}`}>{part.slice(1, -1)}</code>;
        }

        return <span key={`${part}-${index}`}>{part}</span>;
      })}
    </>
  );
}

function ChangelogPage() {
  const entries = clovisChangelog.entries;
  const latest = entries[0];

  return (
    <>
      <section className="changelog-actions" aria-label="Release links">
        <a className="button" href={`${site.githubUrl}/releases`}>
          <Github size={16} aria-hidden="true" />
          GitHub releases
        </a>
        <a className="button" href={`${site.githubUrl}/blob/main/CHANGELOG.md`}>
          <FileJson size={16} aria-hidden="true" />
          Source changelog
        </a>
      </section>

      {latest ? (
        <section className="changelog-latest" aria-labelledby="latest-release-title">
          <div>
            <span>Latest release</span>
            <h2 id="latest-release-title">v{latest.version}</h2>
            <p>{latest.summary}</p>
          </div>
          <time dateTime={latest.date}>{formatReleaseDate(latest.date)}</time>
        </section>
      ) : null}

      <section className="changelog-list" aria-label="All release notes">
        {entries.map((entry) => (
          <article className="changelog-entry" id={`v${entry.version}`} key={entry.version}>
            <header>
              <div>
                <a href={`#v${entry.version}`}>v{entry.version}</a>
                <time dateTime={entry.date}>{formatReleaseDate(entry.date)}</time>
              </div>
              <p>{entry.summary}</p>
            </header>
            <ul>
              {entry.items.map((item) => (
                <li key={item}>
                  <InlineCodeText text={item} />
                </li>
              ))}
            </ul>
          </article>
        ))}
      </section>
    </>
  );
}

function TermsPage() {
  return (
    <LegalPage title="Terms of Service" updated="June 11, 2026">
      <h2>Plain-language summary</h2>
      <p>
        Clovis is a public local-first bookkeeping project. You can read the source, install the package, and keep your
        ledger on your machine. Clovis Computing does not operate as your bank, accountant, broker, tax preparer,
        investment adviser, fiduciary, payment processor, or custodian. You are responsible for the records you create,
        the commands you run, the tools you connect, and the decisions you make from any Clovis output.
      </p>
      <h2>Acceptance of these terms</h2>
      <p>
        These Terms of Service govern access to and use of the {site.domain} website and Clovis materials provided by{" "}
        {site.company}, including documentation, support materials, links, examples, package metadata, and project
        resources. By accessing the website, installing the package, using the CLI or MCP server, copying project
        materials, or communicating with support, you agree to these terms to the maximum extent permitted by applicable
        law.
      </p>
      <p>
        If you accept these terms on behalf of a company, organization, household, client, or other person, you represent
        that you have authority to bind that party. If you do not agree, do not use the website, package, documentation,
        support channels, or related project materials.
      </p>
      <h2>Relationship to the open-source license</h2>
      <p>
        The Clovis source code and public package are licensed under the license published with the repository and npm
        package, currently AGPL-3.0-or-later. That license controls your rights to copy, modify, distribute, run,
        network-deploy, or embed the software. These website terms do not replace, narrow, or expand the open-source
        license. If these terms conflict with the open-source license for the code itself, the open-source license
        controls for the code.
      </p>
      <h2>Scope of Clovis</h2>
      <p>
        Clovis is software for local bookkeeping workflows. It may help you import statement files, store records in a
        local ledger, run reports, inspect transactions, reconcile records, create backups, export data, expose typed
        tools to local AI clients, and answer questions from records you control. Clovis is not a hosted finance account
        and does not custody money, move money, guarantee account balances, verify bank records, file taxes, provide
        regulated advice, or make financial decisions for you.
      </p>
      <h2>Your responsibilities</h2>
      <p>You are solely responsible for:</p>
      <ul>
        <li>reviewing imported transactions and generated reports before relying on them;</li>
        <li>reconciling Clovis records against source statements and official account records;</li>
        <li>choosing account names, categories, budgets, exchange rates, assumptions, and reporting methods;</li>
        <li>confirming tax, legal, accounting, investment, payroll, and compliance treatment with qualified advisers;</li>
        <li>maintaining backups and access controls for local ledgers, exports, statement files, and devices;</li>
        <li>protecting credentials, API keys, private files, account numbers, and sensitive financial records;</li>
        <li>testing Clovis before using it in production, business-critical, client-facing, or regulated workflows; and</li>
        <li>complying with all laws, regulations, contracts, employer policies, client obligations, and platform terms that apply to you.</li>
      </ul>
      <h2>No professional advice or fiduciary relationship</h2>
      <p>
        Clovis output is for informational and organizational use only. Clovis Computing does not provide financial,
        accounting, tax, audit, legal, payroll, benefits, credit, banking, insurance, securities, investment, business,
        or professional advice through the website, package, README, CLI, MCP server, examples, support messages, or AI
        integrations. No attorney-client, accountant-client, adviser-client, fiduciary, employment, agency, partnership,
        joint venture, or trustee relationship is created by your use of Clovis.
      </p>
      <h2>AI, MCP, automation, and agents</h2>
      <p>
        Clovis can expose local bookkeeping tools to AI clients through MCP or other local integrations. AI systems,
        agents, scripts, prompts, models, and tool calls can misunderstand your data, omit relevant context, produce
        incorrect summaries, recommend improper categories, trigger unintended commands, or generate unsafe outputs. You
        are responsible for configuring AI clients, reviewing tool permissions, inspecting proposed actions, validating
        results, and deciding what information is sent to any AI provider or local model.
      </p>
      <h2>Local data and backups</h2>
      <p>
        Clovis is designed around local ledger files. {site.company} does not maintain a hosted ledger account for you,
        does not automatically receive your local financial records, and does not guarantee recovery of lost, deleted,
        corrupted, encrypted, or overwritten local files. You are responsible for backups, restore testing, device
        security, malware protection, file permissions, version control, retention policies, and disaster recovery.
      </p>
      <h2>Compatibility and data migration risk</h2>
      <p>
        Clovis 1.x documents compatibility expectations for public package exports, CLI commands, MCP tools, and readable
        migrations. Reports, import behavior, examples, docs, dependencies, operating systems, Node.js versions, or user
        modifications may still affect behavior or data compatibility. Before upgrading or using Clovis in an important
        workflow, back up your data, test on copies, read release notes, and confirm the output.
      </p>
      <h2>Third-party services and dependencies</h2>
      <p>
        Clovis may be distributed, discussed, installed, hosted, secured, or used through third-party services such as
        GitHub, npm, Cloudflare, email providers, operating systems, package managers, AI providers, MCP clients,
        banks, statement exporters, and dependency packages. Those services are not controlled by {site.company}. Their
        availability, security, privacy, pricing, terms, outages, rate limits, moderation, account access, package
        delivery, and data handling are governed by their own terms and policies.
      </p>
      <h2>User content, support submissions, and feedback</h2>
      <p>
        If you send issues, support requests, examples, logs, code, pull requests, comments, ideas, feature requests, or
        other materials to Clovis Computing or public project channels, you represent that you have the right to share
        them and that they do not violate third-party rights or expose information you are not authorized to disclose.
        Do not send private financial records, credentials, account numbers, API keys, secrets, or full statement files
        unless a safe private process has been agreed.
      </p>
      <p>
        You grant {site.company} a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, publish,
        display, distribute, and create derivative works from support submissions and feedback for operating,
        improving, securing, documenting, and supporting Clovis, subject to any open-source contribution terms that apply
        to code contributions.
      </p>
      <h2>Acceptable use</h2>
      <p>You may not use Clovis, this website, package distribution, support channels, or project resources to:</p>
      <ul>
        <li>violate any law, regulation, court order, contract, intellectual-property right, privacy right, or platform policy;</li>
        <li>commit fraud, money laundering, sanctions evasion, unauthorized trading, tax evasion, or other unlawful financial activity;</li>
        <li>misrepresent Clovis output as professional advice, audited financial statements, tax filings, investment recommendations, or bank records;</li>
        <li>attack, scan, overload, reverse engineer unlawfully, bypass access controls, or interfere with systems or accounts;</li>
        <li>upload, publish, or distribute malware, malicious packages, credential stealers, exploit code, or deceptive content;</li>
        <li>collect, disclose, or process another person&apos;s financial data without authority;</li>
        <li>remove required notices, misstate licensing obligations, or imply endorsement by Clovis Computing without permission; or</li>
        <li>use Clovis in safety-critical, emergency, life-support, medical, weapons, nuclear, aviation, or other high-risk systems where failure could cause serious harm.</li>
      </ul>
      <h2>Security reporting</h2>
      <p>
        If you believe you found a security vulnerability, report it privately through the support contact or GitHub
        security reporting channel when available. Do not publicly disclose a vulnerability, access data that is not
        yours, degrade service, exfiltrate secrets, or test against systems you do not own or have permission to test.
        Responsible reports are welcome, but no bug bounty, reward, employment, contract, or payment obligation exists
        unless agreed in writing.
      </p>
      <h2>Changes, suspension, and discontinuation</h2>
      <p>
        {site.company} may change, suspend, restrict, or discontinue the website, documentation, support channels,
        examples, releases, package publication, project materials, or future services at any time, with or without
        notice, to the maximum extent permitted by law. You are responsible for maintaining copies and backups of
        materials needed for your workflows, subject to applicable licenses.
      </p>
      <h2>Paid offers, commercial licenses, and separate agreements</h2>
      <p>
        The public package is currently available through npm and GitHub. Future paid products, commercial licenses,
        hosted services, support plans, consulting, or enterprise agreements may have separate terms, order forms, or
        written agreements. If a separate written agreement applies, it controls where it expressly conflicts with these
        terms.
      </p>
      <h2>Intellectual property and branding</h2>
      <p>
        Except for rights granted by the open-source license or a separate written agreement, {site.company} and its
        licensors retain all rights in Clovis names, marks, logos, website copy, documentation, design, and project
        materials. You may not imply sponsorship, endorsement, partnership, certification, or official status without
        written permission.
      </p>
      <h2>Export controls and sanctions</h2>
      <p>
        You are responsible for complying with export-control, sanctions, import, encryption, financial, and technology
        laws that apply to your use, download, distribution, modification, or deployment of Clovis. Do not use Clovis if
        applicable law prohibits you from doing so.
      </p>
      <h2>Indemnification</h2>
      <p>
        To the maximum extent permitted by law, you agree to defend, indemnify, and hold harmless {site.company}, its
        owners, maintainers, contributors, affiliates, service providers, and representatives from and against claims,
        damages, losses, liabilities, costs, and expenses, including reasonable legal fees, arising from or related to
        your use of Clovis, your local data, your automation, your AI-client configuration, your violation of these
        terms, your violation of law or third-party rights, your content or submissions, or your use of Clovis for
        professional, commercial, client, regulated, or high-risk workflows.
      </p>
      <h2>Disclaimer of warranties</h2>
      <p>
        To the maximum extent permitted by law, Clovis, the website, documentation, package, examples, support, source,
        CLI, MCP server, APIs, reports, imports, exports, schemas, migrations, AI integrations, and all related
        materials are provided &quot;as is,&quot; &quot;as available,&quot; and with all faults. {site.company} disclaims all
        warranties, express, implied, statutory, or otherwise, including warranties of merchantability, fitness for a
        particular purpose, title, non-infringement, accuracy, availability, security, compatibility, quiet enjoyment,
        uninterrupted operation, and error-free operation.
      </p>
      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, {site.company}, its owners, maintainers, contributors, affiliates,
        service providers, and representatives will not be liable for indirect, incidental, special, consequential,
        exemplary, punitive, enhanced, or similar damages; lost profits; lost revenue; lost savings; lost opportunity;
        lost goodwill; data loss; business interruption; account errors; tax, legal, accounting, investment, trading, or
        business decisions; failed imports; incorrect reports; security incidents; third-party services; AI output; or
        unauthorized use of your device, credentials, files, or ledger, even if advised of the possibility of those
        damages.
      </p>
      <p>
        To the maximum extent permitted by law, the total aggregate liability of {site.company} and related parties for
        all claims arising from or related to Clovis, the website, support, or these terms will not exceed the greater of
        the amount you paid directly to {site.company} for the specific Clovis product or service giving rise to the
        claim in the twelve months before the event giving rise to liability, or 100 USD.
      </p>
      <h2>Release</h2>
      <p>
        To the maximum extent permitted by law, you release {site.company} and related parties from claims arising from
        your local records, imported files, third-party services, AI providers, open-source modifications, downstream
        deployments, package registry behavior, source repository behavior, or financial decisions made from Clovis
        output.
      </p>
      <h2>Disputes</h2>
      <p>
        You agree to try to resolve disputes informally by emailing {site.supportEmail} before filing a claim, unless
        applicable law allows you to proceed without notice. To the maximum extent permitted by law, disputes must be
        brought individually and not as a plaintiff or class member in any class, collective, consolidated, private
        attorney general, or representative action. Unless a separate written agreement says otherwise, disputes will be
        handled by courts with jurisdiction over {site.company}&apos;s principal place of business or another forum
        required by non-waivable applicable law.
      </p>
      <h2>Severability and no waiver</h2>
      <p>
        If any part of these terms is found unenforceable, the remaining parts remain in effect to the maximum extent
        permitted by law. Failure to enforce a provision is not a waiver. Headings are for convenience only.
      </p>
      <h2>Changes to these terms</h2>
      <p>
        We may update these terms as the project changes. The updated date shows when the current version was posted.
        Continued use after changes means you accept the updated terms to the maximum extent permitted by law.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about these terms can be sent to <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>
    </LegalPage>
  );
}

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" updated="June 11, 2026">
      <h2>Plain-language summary</h2>
      <p>
        Clovis is designed so your ledger can stay on your machine. The public website does not operate a hosted Clovis
        account for your transactions, balances, budgets, reports, or imported statements. This policy explains what
        Clovis Computing controls, what it does not control, and what you should avoid sending through public support
        channels.
      </p>
      <h2>What this policy covers</h2>
      <p>
        This policy covers the {site.domain} website, support email, and project communications controlled by{" "}
        {site.company}. It does not control GitHub, npm, Cloudflare, AI providers, your MCP client, your terminal,
        your operating system, your bank, or any other third-party service.
      </p>
      <h2>Local-first financial data</h2>
      <p>
        Clovis is built around local ledger files and statement imports. We do not automatically receive, host, sync,
        inspect, sell, or process your ledger, transaction history, statement files, account balances, categories,
        budgets, reports, backups, account numbers, merchant history, tax records, or financial exports simply because
        you visit the website, install the package, clone the repository, or run Clovis locally.
      </p>
      <h2>Information you choose to send</h2>
      <p>
        We may receive information you choose to provide, including support emails, GitHub issue content, pull requests,
        comments, security reports, screenshots, logs, reproduction steps, package versions, operating-system details,
        command output, example files, business inquiries, and contact information. Public GitHub issues and pull
        requests may be visible to anyone.
      </p>
      <p>
        Do not send private financial records, full bank statements, account numbers, login credentials, API keys,
        secrets, seed phrases, personal identification documents, tax records, payroll records, client data, medical
        information, or other highly sensitive data unless we have specifically agreed to a safe private process and you
        have redacted anything unnecessary.
      </p>
      <h2>Website technical data</h2>
      <p>
        Like most websites, hosting and security providers may process technical request data such as IP address,
        browser information, device information, requested URL, referrer, timestamps, and basic security logs. We use
        this information to serve the website, maintain reliability, troubleshoot issues, and protect against abuse.
      </p>
      <p>
        We use Cloudflare Web Analytics and related Cloudflare dashboard metrics to understand aggregate page traffic,
        performance, referrers, request patterns, crawler activity, AI crawler activity, and abuse signals for the public
        website. These analytics are for operating and improving the website; they are not used to inspect local Clovis
        ledger files, statements, prompts, account names, account numbers, license keys, balances, budgets, reports, or
        financial records.
      </p>
      <h2>Cookies and similar technologies</h2>
      <p>
        The current public website is intended to be a simple static site and does not intentionally place advertising
        cookies or operate a hosted Clovis user account. Cloudflare Web Analytics may load a performance beacon to
        measure page views and browser performance. Hosting, security, browser, or third-party link destinations may
        still use logs, cookies, local storage, or similar technologies under their own policies. If we add account
        features, payments, hosted services, or marketing tools later, this policy should be updated before or when
        those features are introduced.
      </p>
      <h2>GitHub, npm, and AI providers</h2>
      <p>
        GitHub and npm process data under their own policies when you view the repository, open an issue, publish
        comments, install packages, or use registry services. If you connect Clovis to an AI client or provider, the
        information sent to that provider depends on your local client, your prompt, your configuration, and the tools
        you enable.
      </p>
      <h2>AI and local tools</h2>
      <p>
        Clovis can expose local bookkeeping tools to MCP-compatible clients. Clovis Computing does not automatically
        receive prompts, model outputs, tool calls, ledger rows, or statement files from your local MCP use. However,
        your AI client, model provider, operating system, logging tools, or other integrations may process that
        information depending on your configuration. Review those tools before connecting them to financial records.
      </p>
      <h2>How we use information</h2>
      <p>We use website, support, and project communication information to:</p>
      <ul>
        <li>respond to support requests and project questions;</li>
        <li>operate, debug, secure, and improve the website and project;</li>
        <li>investigate abuse, security reports, or reliability problems;</li>
        <li>triage bugs, reproduce issues, review pull requests, and update documentation;</li>
        <li>communicate about package status, project changes, licensing, or support when you contact us;</li>
        <li>maintain reasonable project and business records; and</li>
        <li>comply with legal obligations when required.</li>
      </ul>
      <h2>Legal bases where required</h2>
      <p>
        Where laws such as GDPR or similar frameworks require a legal basis, we process personal information based on
        one or more of the following: your consent, performance of a contract or pre-contractual request, legitimate
        interests in operating and securing the project, compliance with legal obligations, and protection of rights,
        safety, and security.
      </p>
      <h2>Sharing</h2>
      <p>
        We do not sell financial data. We may share limited website, support, and project communication information with
        service providers that help operate the website, email, package distribution, source hosting, security,
        diagnostics, or legal compliance. We may also disclose information if required by law or to protect the project,
        users, or others.
      </p>
      <h2>Service providers and public platforms</h2>
      <p>
        We may use service providers and public platforms for hosting, domain services, email, source control, package
        distribution, security, diagnostics, and project operations. These providers may process technical, account, or
        communication data as independent controllers, processors, or service providers depending on the relationship
        and the applicable law.
      </p>
      <h2>No sale of personal information</h2>
      <p>
        We do not knowingly sell personal information or share personal information for cross-context behavioral
        advertising through the public Clovis website. We also do not knowingly sell ledger data, statement data, or
        financial records.
      </p>
      <h2>Retention</h2>
      <p>
        We keep support and operational records for as long as needed to respond, operate the project, maintain
        reasonable records, comply with legal obligations, resolve disputes, and prevent abuse. You control local ledger
        files stored on your own machine.
      </p>
      <h2>Security</h2>
      <p>
        We use reasonable administrative, technical, and organizational measures appropriate to a small public software
        project and the information we control. No website, repository, registry, email account, device, package, model,
        or network is perfectly secure. You are responsible for securing your own devices, ledgers, backups,
        credentials, local networks, package installs, AI clients, and files.
      </p>
      <h2>International transfers</h2>
      <p>
        Website, support, repository, package, email, and infrastructure providers may process information in countries
        other than where you live. Those countries may have privacy laws that differ from your location. By using the
        website, support channels, GitHub, npm, or related services, your information may be transferred and processed
        where those providers and Clovis Computing operate.
      </p>
      <h2>Your choices</h2>
      <p>
        You can avoid sending private financial data by running Clovis locally, redacting support examples, and choosing
        what you share in GitHub issues, emails, prompts, and AI-client configurations. You can request support-record
        access or deletion by emailing us, subject to security, legal, and recordkeeping limits.
      </p>
      <h2>Your privacy rights</h2>
      <p>
        Depending on where you live, you may have rights to request access, correction, deletion, portability,
        restriction, objection, withdrawal of consent, or appeal of certain privacy decisions. These rights are subject
        to legal limits, identity verification, security needs, public GitHub record constraints, open-source project
        integrity, and retention obligations.
      </p>
      <h2>Children</h2>
      <p>
        Clovis is not directed to children. Do not use the website, package, support channels, or project resources if
        you are not old enough to enter into these terms or provide personal information under the laws that apply to
        you.
      </p>
      <h2>Changes to this policy</h2>
      <p>
        We may update this policy as Clovis, the website, support channels, paid offerings, or service providers change.
        The updated date shows when the current version was posted.
      </p>
      <h2>Contact</h2>
      <p>
        Privacy questions can be sent to <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>
    </LegalPage>
  );
}

function RefundPage() {
  return (
    <LegalPage title="Refund Policy" updated="June 11, 2026">
      <h2>Plain-language summary</h2>
      <p>
        Clovis is currently distributed as a public npm package and open-source GitHub project. There is no direct paid
        website checkout for the public package today. If no payment was made directly to Clovis Computing, there is no
        Clovis Computing payment to refund.
      </p>
      <h2>Public package and source</h2>
      <p>
        The public npm package and GitHub source repository are available without a Clovis website purchase. Because no
        direct payment is collected by {site.company} for those public resources, there is no Clovis website payment to
        refund for installing the package, cloning the repository, or reading the documentation.
      </p>
      <h2>No warranty-based refund right</h2>
      <p>
        The public package is provided under its open-source license and related terms. Downloading, installing,
        forking, cloning, modifying, or using the public package does not create a paid subscription, hosted account,
        service-level commitment, uptime commitment, support entitlement, professional-services engagement, or refund
        obligation from {site.company}.
      </p>
      <h2>Third-party charges</h2>
      <p>
        If you incur charges from another provider, such as npm, GitHub, an AI provider, cloud hosting, a bank export
        service, or another tool used with Clovis, that provider controls its own billing and refund process.
      </p>
      <h2>Donations, sponsorships, and voluntary payments</h2>
      <p>
        If {site.company} later accepts donations, sponsorships, grants, tips, or other voluntary payments, those
        payments may be non-refundable unless the payment page or written agreement says otherwise. Payment processor
        fees, currency conversion, taxes, and third-party charges may be outside {site.company}&apos;s control.
      </p>
      <h2>Commercial licenses and custom agreements</h2>
      <p>
        Commercial licenses, enterprise terms, hosted services, support plans, consulting, implementation assistance,
        security reviews, or other custom arrangements may have their own payment, renewal, cancellation, and refund
        terms. The signed order form, invoice, statement of work, or written agreement will control those purchases.
      </p>
      <h2>Future paid products</h2>
      <p>
        If {site.company} offers paid products, commercial licenses, hosted services, support plans, or direct purchases
        in the future, the refund terms for that purchase will be stated at or before checkout or in the applicable
        agreement. Those terms will control that paid offer.
      </p>
      <h2>Taxes, fees, and payment processors</h2>
      <p>
        Taxes, payment processor fees, chargeback fees, bank fees, currency conversion, and platform fees may be
        non-refundable or handled by third parties. Any refund that becomes available may exclude amounts that
        {site.company} cannot recover or is required to retain by law.
      </p>
      <h2>Abuse and fraud</h2>
      <p>
        {site.company} may deny refund requests, support requests, or account-related requests connected to fraud,
        abuse, chargeback misuse, policy violations, sanctions restrictions, unlawful activity, or attempts to obtain
        services without payment.
      </p>
      <h2>Questions</h2>
      <p>
        Billing or refund questions can be sent to <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
        Include the product, purchase date, receipt or invoice identifier, and the email address used for purchase if a
        paid Clovis offer exists.
      </p>
    </LegalPage>
  );
}

function SupportPage() {
  return (
    <PageFrame
      eyebrow="Support"
      title="Support"
      lead="Find the docs, README, source repository, npm package, issue tracker, and support contact for Clovis."
      icon={Mail}
    >
      <section className="support-grid">
        {supportLinks.map((link) => {
          const Icon = link.icon;
          return (
            <a className="support-card" key={link.href} href={link.href}>
              <Icon size={22} aria-hidden="true" />
              <span>{link.label}</span>
              <ExternalLink size={16} aria-hidden="true" />
            </a>
          );
        })}
      </section>
      <section className="support-section">
        <h2>Start here</h2>
        <p>
          For setup and usage questions, start with the generated docs. They cover installation, CLI commands, MCP
          setup, agent references, and the current tool catalog from the npm package.
        </p>
        <p>
          Use the README for source-level context, release notes, contribution details, and broader examples of
          questions Clovis can answer.
        </p>
        <p>
          Use GitHub issues for reproducible bugs, documentation gaps, feature requests, and public technical
          questions. Use email for sensitive security reports, private business inquiries, or anything that should not
          be indexed publicly.
        </p>
      </section>
      <section className="support-section">
        <h2>What to include</h2>
        <p>
          Include the topic, package version, Node version, operating system, command you ran, expected result, actual
          result, and whether you are using the CLI, package APIs, or MCP server.
        </p>
        <p>
          For import issues, include the file type, bank export format, account type, and redacted sample rows when
          possible. Do not include bank credentials, API keys, full statement files, account numbers, or private
          financial records in public GitHub issues.
        </p>
        <ul>
          <li>Good: a minimal command, package version, redacted sample row, and exact error message.</li>
          <li>Bad: an unredacted bank statement, private ledger file, access token, or screenshot with account numbers.</li>
        </ul>
      </section>
      <section className="support-section">
        <h2>Security and private reports</h2>
        <p>
          Send sensitive issues to <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a> instead of posting
          them publicly. Include a clear description, reproduction steps, affected version, impact, and a safe proof of
          concept if you have one.
        </p>
        <p>
          Do not access data that is not yours, degrade services, publish exploit details before coordination, or test
          against third-party systems without permission. Security reports do not create an employment, consulting,
          bounty, reward, or payment relationship unless agreed in writing.
        </p>
      </section>
      <section className="support-section">
        <h2>Support scope</h2>
        <p>
          Support can help with package usage, documentation gaps, bug reports, local setup, and public project
          questions. Clovis support cannot provide financial, tax, legal, investment, accounting, banking, or emergency
          advice, and cannot recover local ledger files that were not backed up.
        </p>
        <p>
          Support is provided as available. The public package does not include guaranteed response times, uptime
          commitments, compatibility guarantees, migration guarantees, professional review, or dedicated support unless a
          separate written agreement says otherwise.
        </p>
      </section>
      <section className="support-section">
        <h2>Before sharing data</h2>
        <p>
          Redact names, account numbers, balances, transaction IDs, addresses, phone numbers, emails, tax identifiers,
          payroll information, client records, and anything else that is not needed to reproduce the issue. Prefer
          synthetic examples when possible.
        </p>
      </section>
    </PageFrame>
  );
}

function PageFrame({
  children,
  eyebrow,
  title,
  lead,
  icon: Icon
}: {
  children: React.ReactNode;
  eyebrow: string;
  title: string;
  lead: string;
  icon: LucideIcon;
}) {
  return (
    <>
      <section className="page-hero">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{lead}</p>
        </div>
        <Icon size={52} aria-hidden={true} />
      </section>
      <div className="page-content">{children}</div>
    </>
  );
}

function LegalPage({ children, title, updated }: { children: React.ReactNode; title: string; updated: string }) {
  return (
    <PageFrame eyebrow="Legal" title={title} lead={`Last updated ${updated}.`} icon={ShieldCheck}>
      <article className="legal-copy">{children}</article>
    </PageFrame>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div>
        <a className="footer-brand" href="/">
          <img src="/assets/clovis-icon.png" alt="" width="28" height="28" />
          <span>{site.company}</span>
        </a>
        <p>{site.domain}</p>
      </div>
      <nav aria-label="Footer navigation">
        {footerRoutes.map((route) => (
          <a key={route.path} href={route.path}>
            {route.label}
          </a>
        ))}
      </nav>
      <a className="footer-support" href={`mailto:${site.supportEmail}`}>
        <Mail size={16} aria-hidden="true" />
        {site.supportEmail}
      </a>
    </footer>
  );
}
